import React, { useEffect, useRef, useState, createContext, useContext, useMemo, Fragment } from 'react'

import Meta from '@/components/meta'
import Footer from '@/components/footer'
import HelpHeader from '@/components/help-header'
import HelpNav from '@/components/help-nav'
import TocNav from '@/components/toc-nav'

import CloudStatus from '@/components/cloud-status'
import NavContainer from '@/components/nav-container'
import HelpUsefulLinks from '@/components/help/help-useful-links'
import CopyPageDropdown from '@/components/ui/copy-page-dropdown'
import HelpContactForm from '@/components/help/help-contact-form'
import SearchModal from '@/components/search/search-modal'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'

import Hamburger from '@/icons/bwi/hamburger.svg'
import { slugify } from '@/utils/strings'
import { cx } from '@/utils/strings'
import { TypeHelpArticle, TypeHelpTopNav } from '@/types/ctf'
import { filterFalsy } from '@/utils/array'
import { localizedPath, useLocale } from '@/utils/locale'

type HelpParentProps = {
  children: React.ReactNode
  footerNav: any
  newsletterForm: any
  title: string
  seo: any
  helpNav: TypeHelpTopNav<'WITHOUT_UNRESOLVABLE_LINKS'>[]
  helpNavInitial?: any
  searchInHeader?: boolean
  headerBanners?: any
  toc?: any
  isHelpHome?: boolean
  customBreadcrumbs?: TypeHelpArticle<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['customBreadcrumbs']
}

// Create context for search modal
export const HelpSearchContext = createContext({
  isSearchOpen: false,
  setSearchOpen: (open: boolean) => {},
})
export const useHelpSearchModal = () => {
  const { isSearchOpen, setSearchOpen } = useContext(HelpSearchContext)
  const openSearch = () => setSearchOpen(true)
  const closeSearch = () => setSearchOpen(false)
  return { isSearchOpen, setSearchOpen, openSearch, closeSearch }
}

export default function HelpParent({
  children,
  footerNav,
  newsletterForm,
  title,
  seo,
  helpNav,
  helpNavInitial = [],
  searchInHeader = false,
  headerBanners = [],
  toc = [],
  isHelpHome = false,
  customBreadcrumbs,
}: HelpParentProps) {
  const headerRef = useRef(null)
  const [isNavOpen, setNavOpen] = useState(false)
  const [isSearchModalOpen, setSearchModalOpen] = useState(false)

  const [helpContactFormOpen, setHelpContactFormOpen] = useState(false)
  const supportRef = useRef<HTMLDivElement>(null)

  const { currentLocale } = useLocale()
  const baseHelpUrl = localizedPath('/help/', currentLocale)

  useEffect(() => {
    const { hash } = window.location
    if (hash === '#contact-support') {
      setHelpContactFormOpen(true)
    }

    // Show modal if search query is present in URL
    const searchParams = new URLSearchParams(window.location.search)
    const query = searchParams.get('q')
    if (query) {
      setSearchModalOpen(true)
    }
  }, [])

  useEffect(() => {
    if (helpContactFormOpen)
      supportRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
  }, [helpContactFormOpen, supportRef.current])

  const hasToc = toc && toc.length > 0

  const getTopNavSlug = (nav: TypeHelpTopNav<'WITHOUT_UNRESOLVABLE_LINKS'>) => {
    return nav.fields.slug ? `${baseHelpUrl}${nav.fields.slug}/` : baseHelpUrl
  }
  const currentTopHelpNav = useMemo(() => {
    return [...helpNav]
      .sort((a, b) => {
        // sort by slug length (desc)
        return (b.fields.slug?.length || 0) - (a.fields.slug?.length || 0)
      })
      .find((nav) => {
        return new RegExp(`^${getTopNavSlug(nav)}`).test(window.location.pathname)
      })
  }, [helpNav])
  const currentHelpNavs = filterFalsy(currentTopHelpNav?.fields.items || [])

  const breadcrumbs = (helpNavInitial?.length || 0) > 0 ? helpNavInitial : [{ title: currentTopHelpNav?.fields.title }]

  return (
    <HelpSearchContext.Provider
      value={{
        isSearchOpen: isSearchModalOpen,
        setSearchOpen: setSearchModalOpen,
      }}
    >
      <Meta seo={seo} hasMd />
      <section className="pb-10 lg:pb-20">
        <HelpHeader
          ref={headerRef}
          hasLeftLinks={false}
          hasSearch={searchInHeader}
          twRoot={`bg-indigoBlue dark:bg-secondary-900`}
          headerBanners={headerBanners}
          isHelpHome={isHelpHome}
          topNavs={helpNav}
          activeTopNavId={currentTopHelpNav?.sys.id}
        />
        <div className="flex max-w-8xl mx-auto mt-2 px-5 gap-4">
          {helpNav?.map((nav) => (
            <CtaLink
              key={nav.sys.id}
              to={getTopNavSlug(nav)}
              style={buttonStyles.btnText}
              className={cx(
                'text-base no-underline! px-1 py-1',
                "after:content-[''] after:block after:w-full after:h-0.5 after:rounded",
                'after:scale-x-0 after:origin-left after:transition-transform after:duration-200 hover:after:scale-x-100 focus:after:scale-x-100 after:bg-current',
                nav.sys.id === currentTopHelpNav?.sys.id
                  ? 'after:scale-x-100 after:bg-primaryBlue'
                  : 'hover:text-secondary-500 focus:text-secondary-500 dark:hover:text-secondary-300 dark:focus:text-secondary-300'
              )}
            >
              {nav.fields.title}
            </CtaLink>
          ))}
        </div>
        <div className="md:flex max-w-[1600px] m-auto px-5 xl:pr-0">
          <div className="flex-none lg:w-[235px] xl:w-[300px] lg:pt-3">
            <NavContainer
              backgroundColor="bg-secondary-50 dark:bg-secondary-800"
              position="left"
              isDrawerOpen={isNavOpen}
              onRequestClose={() => {
                setNavOpen(false)
              }}
              className="lg:sticky lg:rounded-2xl"
              headerRef={headerRef}
              drawerBreakpoint="lg"
            >
              <HelpNav
                helpNav={currentHelpNavs}
                helpNavInitial={helpNavInitial}
                indexLink={{
                  title: currentTopHelpNav?.fields.indexTitle || 'Home',
                  slug: currentTopHelpNav?.fields.slug || '',
                }}
                currentTopNav={currentTopHelpNav?.fields.title || 'Help'}
              />
            </NavContainer>
          </div>

          <div className="min-w-0 grow md:pr-5 lg:px-5 xl:px-10 relative">
            <div className={cx('flex items-center pt-4', !hasToc && 'place-content-end')}>
              <button onClick={() => setNavOpen(!isNavOpen)} id="openHelpMenu" className="w-9 h-9 mr-3 block lg:hidden">
                <Hamburger />
              </button>

              {/* Breadcrumbs */}
              <div className="py-2 text-secondary-600 dark:text-secondary-300 text-sm font-bold md:block grow uppercase">
                {(!customBreadcrumbs || customBreadcrumbs.length === 0) &&
                  breadcrumbs
                    .map((nav: any) => nav.title)
                    .reduce((prev: any, curr: any) => [
                      prev,
                      <span key={slugify(prev)} className="font-bwi mx-0.5">
                        &#xe91b;
                      </span>,
                      curr,
                    ])}
                {customBreadcrumbs &&
                  customBreadcrumbs.length > 0 &&
                  customBreadcrumbs.map(
                    (link, index) =>
                      link?.fields && (
                        <Fragment key={link.sys.id}>
                          <CtaLink {...link.fields} to={link.fields.link?.fields.url} className="no-underline! text-sm">
                            {link.fields.label}
                          </CtaLink>
                          {index < customBreadcrumbs.length - 1 && <span className="font-bwi mx-0.5">&#xe91b;</span>}
                        </Fragment>
                      )
                  )}
              </div>
              <div className="hidden sm:block md:hidden lg:pt-2 lg:mb-2 xl:hidden">
                <div className="flex gap-4">
                  <div>
                    <HelpContactForm />
                  </div>
                  {!isHelpHome && <CopyPageDropdown title={title} />}
                </div>
              </div>
            </div>
            {hasToc && (
              <div className="md:hidden">
                <TocNav sections={toc} />
              </div>
            )}
            <article className="">{children}</article>
          </div>
          {/* help right side bar */}
          <div className="hidden md:block md:flex-none w-[220px] py-5 xl:w-[280px] xl:p-5 xl:pl-0">
            <div className="md:flex md:flex-col md:gap-5">
              <HelpUsefulLinks isHelpHome={!!isHelpHome} links={currentTopHelpNav?.fields.usefulLinks || []} />
              {!isHelpHome && (
                <div>
                  <CopyPageDropdown title={title} />
                </div>
              )}
              <HelpContactForm isButtonStyle />
              {currentTopHelpNav?.fields.displayCloudStatus !== false && (
                <CloudStatus className="rounded-lg p-4 [h3]:text-lg [&*>div]:w-auto! [&*>div]:z-10 dark:bg-secondary-800 [&_h3]:text-base [&_h3]:font-bold [&_a]:text-sm xl:[&_a]:text-base" />
              )}
            </div>
            {hasToc && (
              <div className="sticky top-20 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <TocNav sections={toc} />
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer footerNav={footerNav} newsletterForm={newsletterForm} />
      <SearchModal searchType="help" isOpen={isSearchModalOpen} onClose={() => setSearchModalOpen(false)} />
    </HelpSearchContext.Provider>
  )
}
